import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Modal, FileCCE } from "../components/ui";
import PDFButton from "../components/PDFButton";
import { Sparkles, BookOpen, FileText, Save, GripVertical, Plus, Trash2, Pencil } from "lucide-react";

export default function Procedures({ embedded = false }) {
  const [procedures, setProcedures] = useState([]);
  const [selected, setSelected] = useState(null);

  async function load() {
    const { data } = await api.get("/data/procedures");
    setProcedures(data.sort((a, b) => (a.code || "").localeCompare(b.code || "")));
  }
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="procedures-module">
      {!embedded && (
        <div className="flex items-center justify-between mb-3">
          <h1 className="card-h1" style={{ margin: 0 }}>Procedimientos Ambientales</h1>
          <PDFButton endpoint="/pdf/module/procedures" filename="reporte_procedimientos.pdf" label="Reporte PDF" />
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {procedures.map((p) => (
          <div key={p.id} className="card" style={{ cursor: "pointer" }} onClick={() => setSelected(p)} data-testid={`proc-card-${p.code}`}>
            <div className="flex items-start gap-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--bg-3)", display: "grid", placeItems: "center" }}>
                <BookOpen size={20} className="text-accent" />
              </div>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <div className="font-semibold text-sm">{p.code}</div>
                <div className="text-xs text-dim mt-2" style={{ lineHeight: 1.4 }}>{p.name}</div>
                <div className="text-xs mt-2">
                  <span className="badge badge-blue">{p.version || "1.0"}</span>
                  {" "}<span className="text-dim">próx. {p.next_review || "-"}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!selected} onClose={() => { setSelected(null); load(); }} title={`${selected?.code} — ${selected?.name}`} size="xl">
        {selected && <ProcedureDetail procedure={selected} />}
      </Modal>
    </div>
  );
}

function ProcedureDetail({ procedure }) {
  const [tab, setTab] = useState("split");
  return (
    <div>
      <div className="tabs-bar">
        <button className={`tab ${tab === "split" ? "active" : ""}`} onClick={() => setTab("split")}>Visor + Checklist</button>
        <button className={`tab ${tab === "checklist" ? "active" : ""}`} onClick={() => setTab("checklist")}>Checklist</button>
        <button className={`tab ${tab === "data" ? "active" : ""}`} onClick={() => setTab("data")}>Datos generales</button>
        <button className={`tab ${tab === "files" ? "active" : ""}`} onClick={() => setTab("files")}>Documento PDF</button>
        <button className={`tab ${tab === "versions" ? "active" : ""}`} onClick={() => setTab("versions")}>Historial de versiones</button>
        <button className={`tab ${tab === "ai" ? "active" : ""}`} onClick={() => setTab("ai")}><Sparkles size={12} /> Generar con IA</button>
      </div>
      {tab === "split" && <SplitPanel procedure={procedure} />}
      {tab === "checklist" && <ChecklistTable procedure={procedure} />}
      {tab === "data" && <ProcedureData procedure={procedure} />}
      {tab === "files" && <FilesWithVersioning procedure={procedure} onTabChange={setTab} />}
      {tab === "versions" && <VersionHistory procedure={procedure} />}
      {tab === "ai" && <AIChecklist procedure={procedure} />}
    </div>
  );
}

function FilesWithVersioning({ procedure, onTabChange }) {
  return (
    <div>
      <div className="card mb-3" style={{ background: "rgba(29,158,117,0.06)", border: "1px solid var(--accent)" }}>
        <div className="text-sm">
          📌 <b>Versionado de procedimientos:</b> Al subir un PDF nuevo, podrás archivar el checklist actual y regenerar uno nuevo con IA.
        </div>
      </div>
      <FileCCE module="procedures" parentId={procedure.id} onFileUploaded={(file) => {
        if (window.confirm("Se subió un nuevo documento. ¿Crear una nueva versión del procedimiento y archivar el checklist actual?")) {
          api.post(`/procedures/${procedure.id}/new-version`, { file_id: file.id })
            .then((r) => {
              const arch = r.data.version_archived;
              alert(`✅ Versión ${arch.version_label} archivada con ${arch.item_count} ítems (${arch.final_completion_pct}% promedio).\n\nNueva versión activa: ${r.data.new_version}.\n\nVe a la pestaña "Generar con IA" para crear el nuevo checklist.`);
              if (r.data.regenerate_with_ai && onTabChange) onTabChange("ai");
            }).catch((e) => alert("Error: " + (e.response?.data?.detail || e.message)));
        }
      }} />
    </div>
  );
}

function VersionHistory({ procedure }) {
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);

  async function load() {
    try {
      const { data } = await api.get(`/procedures/${procedure.id}/versions`);
      setVersions(data);
    } catch (e) { console.error(e); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [procedure.id]);

  return (
    <div data-testid="version-history">
      <div className="card mb-3">
        <div className="text-sm">
          <b>Versión activa:</b> <span className="badge badge-green">{procedure.version || "1.0"}</span>
          {procedure.issue_date && <span className="text-xs text-dim ml-2"> · Fecha: {new Date(procedure.issue_date).toLocaleDateString("es-MX")}</span>}
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Versión</th><th>Archivada</th><th>Por</th><th># Ítems</th><th>% Cumplimiento final</th><th>Acciones</th></tr></thead>
          <tbody>
            {!versions.length ? <tr><td colSpan={6} className="text-center text-dim p-4">Sin versiones anteriores archivadas.</td></tr> : versions.map((v) => (
              <tr key={v.id}>
                <td><b>{v.version_label}</b></td>
                <td className="text-xs">{new Date(v.archived_at).toLocaleDateString("es-MX")}</td>
                <td className="text-xs">{v.archived_by_name}</td>
                <td>{v.item_count}</td>
                <td>
                  <span className={`badge ${v.final_completion_pct >= 80 ? "badge-green" : v.final_completion_pct >= 60 ? "badge-yellow" : "badge-red"}`}>
                    {v.final_completion_pct}%
                  </span>
                </td>
                <td><button className="btn btn-sm" onClick={() => setSelected(v)}>Ver checklist</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Checklist archivado — v${selected?.version_label}`} size="xl">
        {selected && (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>#</th><th>Descripción</th><th>Status</th><th>% Avance</th><th>Responsable</th></tr></thead>
              <tbody>
                {(selected.items || []).map((it, i) => (
                  <tr key={i}>
                    <td>{it.number || i + 1}</td>
                    <td className="text-xs">{it.description}</td>
                    <td><span className="badge">{it.status}</span></td>
                    <td>{it.progress || 0}%</td>
                    <td className="text-xs">{it.responsible}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SplitPanel({ procedure }) {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [splitPos, setSplitPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  async function loadFiles() {
    const { data } = await api.get("/files/list", { params: { module: "procedures", parent_id: procedure.id } });
    setFiles(data);
    if (data.length && !selectedFile) setSelectedFile(data[0]);
  }
  useEffect(() => { loadFiles(); /* eslint-disable-next-line */ }, [procedure.id]);

  function onMouseMove(e) {
    if (!dragging) return;
    const wrapper = document.getElementById("split-wrapper");
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setSplitPos(Math.max(20, Math.min(80, pct)));
  }
  useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", () => setDragging(false), { once: true });
    }
    return () => document.removeEventListener("mousemove", onMouseMove);
    // eslint-disable-next-line
  }, [dragging]);

  const pdfUrl = selectedFile ? `${process.env.REACT_APP_BACKEND_URL}/api/files/view/${selectedFile.id}` : null;

  return (
    <div>
      <div className="mb-2 flex gap-2 items-center flex-wrap">
        <label className="label" style={{ margin: 0 }}>Documento:</label>
        <select className="select" style={{ width: 320 }} value={selectedFile?.id || ""} onChange={(e) => setSelectedFile(files.find((f) => f.id === e.target.value))} data-testid="split-pdf-select">
          <option value="">-- Seleccione un PDF --</option>
          {files.map((f) => <option key={f.id} value={f.id}>{f.name || f.original_name}</option>)}
        </select>
        {files.length === 0 && <span className="text-xs text-dim">Sube un PDF en la pestaña "Documento PDF" primero.</span>}
      </div>
      <div id="split-wrapper" style={{ display: "flex", height: 600, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", userSelect: dragging ? "none" : "auto" }}>
        <div style={{ width: `${splitPos}%`, overflow: "auto", background: "var(--bg-2)" }}>
          {pdfUrl ? (
            <iframe src={pdfUrl} title="PDF" style={{ width: "100%", height: "100%", border: "none" }} data-testid="pdf-viewer" />
          ) : (
            <div className="text-center text-dim p-4">Selecciona un PDF para visualizar</div>
          )}
        </div>
        <div
          onMouseDown={() => setDragging(true)}
          style={{ width: 6, background: "var(--border-2)", cursor: "col-resize", flexShrink: 0 }}
          data-testid="split-divider"
        />
        <div style={{ width: `calc(${100 - splitPos}% - 6px)`, overflow: "auto", background: "var(--bg-1)", padding: 12 }}>
          <ChecklistTable procedure={procedure} compact />
        </div>
      </div>
    </div>
  );
}

function ProcedureData({ procedure }) {
  const [form, setForm] = useState({ ...procedure });
  async function save() {
    await api.patch(`/data/procedures/${procedure.id}`, form);
    alert("Guardado.");
  }
  return (
    <div className="grid grid-cols-2 gap-3">
      <div><label className="label">Código</label><input className="input" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
      <div><label className="label">Nombre</label><input className="input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div><label className="label">Versión</label><input className="input" value={form.version || ""} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
      <div><label className="label">Categoría</label><input className="input" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
      <div><label className="label">Fecha emisión</label><input type="date" className="input" value={form.issue_date || ""} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
      <div><label className="label">Próxima revisión</label><input type="date" className="input" value={form.next_review || ""} onChange={(e) => setForm({ ...form, next_review: e.target.value })} /></div>
      <div style={{ gridColumn: "1/-1" }}><label className="label">Responsable</label><input className="input" value={form.responsible || ""} onChange={(e) => setForm({ ...form, responsible: e.target.value })} /></div>
      <div style={{ gridColumn: "1/-1" }}>
        <button className="btn btn-primary" onClick={save}><Save size={14} /> Guardar</button>
      </div>
    </div>
  );
}

function ChecklistTable({ procedure, compact }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/data/checklist_items", {
        params: { procedure_id: procedure.id },
      });
      const sorted = [...data].sort((a, b) => {
        const ao = a.order_index ?? a.number ?? 9999;
        const bo = b.order_index ?? b.number ?? 9999;
        return ao - bo;
      });
      setItems(sorted);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [procedure.id]);

  async function persistOrder(newItems) {
    setItems(newItems);
    try {
      await api.post(`/procedures/${procedure.id}/checklist/reorder`, {
        order: newItems.map((i) => i.id),
      });
    } catch (e) {
      console.error(e);
      load();
    }
  }

  function onDragStart(e, id) { setDragId(id); e.dataTransfer.effectAllowed = "move"; }
  function onDragOver(e, id) { e.preventDefault(); setOverId(id); }
  function onDrop(e, targetId) {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const copy = [...items];
    const fromIdx = copy.findIndex((i) => i.id === dragId);
    const toIdx = copy.findIndex((i) => i.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); setOverId(null); return; }
    const [moved] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, moved);
    setDragId(null); setOverId(null);
    persistOrder(copy);
  }
  function onDragEnd() { setDragId(null); setOverId(null); }

  async function del(row) {
    if (!window.confirm("¿Eliminar este ítem del checklist?")) return;
    await api.delete(`/data/checklist_items/${row.id}`);
    load();
  }

  function openNew() {
    setEditing({
      procedure_id: procedure.id, description: "", criterion: "",
      frequency: "Mensual", status: "No evaluado", progress: 0, responsible: "",
    });
    setShowEdit(true);
  }
  function openEdit(row) { setEditing({ ...row }); setShowEdit(true); }

  async function save() {
    const payload = { ...editing };
    delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at;
    if (editing.id) {
      await api.patch(`/data/checklist_items/${editing.id}`, payload);
    } else {
      await api.post("/data/checklist_items", payload);
    }
    setShowEdit(false); setEditing(null);
    load();
  }

  return (
    <div data-testid={`cl-${procedure.code}-dnd`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-dim">
          <GripVertical size={12} style={{ display: "inline" }} /> Arrastra el ícono ⋮⋮ para reordenar. {items.length} ítems.
        </div>
        <button className="btn btn-sm btn-primary" onClick={openNew} data-testid={`cl-${procedure.code}-add`}>
          <Plus size={12} /> Nuevo ítem
        </button>
      </div>

      <div className="table-wrap">
        {loading ? <div className="p-4 text-dim">Cargando...</div> : !items.length ? (
          <div className="p-4 text-dim text-center">Aún no hay ítems en el checklist.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th style={{ width: 40 }}>#</th>
                <th>Descripción</th>
                {!compact && <th>Criterio</th>}
                <th>Frecuencia</th>
                <th>Status</th>
                <th>% Avance</th>
                {!compact && <th>Responsable</th>}
                <th style={{ width: 90, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr
                  key={it.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, it.id)}
                  onDragOver={(e) => onDragOver(e, it.id)}
                  onDrop={(e) => onDrop(e, it.id)}
                  onDragEnd={onDragEnd}
                  data-testid={`cl-${procedure.code}-row-${it.id}`}
                  style={{
                    cursor: "grab",
                    opacity: dragId === it.id ? 0.4 : 1,
                    background: overId === it.id && dragId && dragId !== it.id
                      ? "rgba(29,158,117,0.12)" : undefined,
                    transition: "background 0.15s",
                  }}
                >
                  <td style={{ cursor: "grab", color: "var(--text-dim)" }} title="Arrastra para reordenar">
                    <GripVertical size={14} />
                  </td>
                  <td><b>{idx + 1}</b></td>
                  <td>{it.description}</td>
                  {!compact && <td className="text-xs">{it.criterion}</td>}
                  <td className="text-xs">{it.frequency}</td>
                  <td>
                    <span className={`badge ${it.status === "Cumple" ? "badge-green" : it.status === "No cumple" ? "badge-red" : it.status === "En proceso" ? "badge-yellow" : ""}`}>{it.status}</span>
                  </td>
                  <td>{it.progress || 0}%</td>
                  {!compact && <td className="text-xs">{it.responsible}</td>}
                  <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                    <div className="flex gap-1 justify-end">
                      <button className="btn btn-sm" onClick={() => openEdit(it)} data-testid={`cl-${procedure.code}-edit-${it.id}`}><Pencil size={12} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => del(it)} data-testid={`cl-${procedure.code}-del-${it.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={editing?.id ? "Editar ítem" : "Nuevo ítem de checklist"} size="lg" testId={`cl-${procedure.code}-modal`}>
        {editing && (
          <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid gap-3">
            <div>
              <label className="label">Descripción *</label>
              <textarea className="textarea" rows={3} required value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
            <div>
              <label className="label">Criterio de verificación</label>
              <textarea className="textarea" rows={2} value={editing.criterion || ""} onChange={(e) => setEditing({ ...editing, criterion: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Frecuencia</label>
                <select className="select" value={editing.frequency || ""} onChange={(e) => setEditing({ ...editing, frequency: e.target.value })}>
                  <option value="">--</option>
                  {["Diaria","Semanal","Mensual","Trimestral","Semestral","Anual","Continua","N/A"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Status</label>
                <select className="select" value={editing.status || "No evaluado"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {["Cumple","No cumple","En proceso","No evaluado"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="label">% Avance</label>
                <input type="number" className="input" value={editing.progress ?? 0} onChange={(e) => setEditing({ ...editing, progress: Number(e.target.value) })} />
              </div>
              <div>
                <label className="label">Responsable</label>
                <input className="input" value={editing.responsible || ""} onChange={(e) => setEditing({ ...editing, responsible: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setShowEdit(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" data-testid={`cl-${procedure.code}-save`}>Guardar</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

function AIChecklist({ procedure }) {
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function generate() {
    if (!text.trim()) { alert("Pega el texto del procedimiento primero."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/checklist/generate", {
        procedure_id: procedure.id,
        name: procedure.name,
        text,
      });
      setItems(data.items || []);
    } catch (e) {
      alert("Error generando checklist: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      await api.post("/ai/checklist/save", { procedure_id: procedure.id, items });
      alert("Checklist guardado. Cambia a la pestaña Checklist.");
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="grid gap-3">
      <div>
        <label className="label">Texto del procedimiento</label>
        <textarea
          className="textarea"
          rows={8}
          placeholder="Pega aquí el contenido del PDF del procedimiento..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="ai-procedure-text"
        />
      </div>
      <div className="flex gap-2">
        <button className="btn btn-primary" onClick={generate} disabled={loading} data-testid="ai-generate-btn">
          {loading ? <span className="spinner" /> : <><Sparkles size={14} /> Generar checklist con IA (Claude)</>}
        </button>
        {items.length > 0 && (
          <button className="btn" onClick={save} disabled={saving} data-testid="ai-save-btn">
            {saving ? <span className="spinner" /> : <><Save size={14} /> Guardar como checklist</>}
          </button>
        )}
      </div>

      {items.length > 0 && (
        <div>
          <div className="text-sm mb-2 text-dim">{items.length} ítems propuestos. Revisa y edita antes de guardar:</div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>#</th><th>Descripción</th><th>Criterio</th><th>Frecuencia</th></tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><input className="input" value={it.description || ""} onChange={(e) => { const c = [...items]; c[i].description = e.target.value; setItems(c); }} /></td>
                    <td><input className="input" value={it.criterion || ""} onChange={(e) => { const c = [...items]; c[i].criterion = e.target.value; setItems(c); }} /></td>
                    <td><input className="input" value={it.frequency || ""} onChange={(e) => { const c = [...items]; c[i].frequency = e.target.value; setItems(c); }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
